/**
 * Styling for the BV UGC expanded ("Shop The Look") view, split out of the
 * components the same way `ugc-landing-page.js` is — the desktop and mobile
 * partials consume these via `useMultiStyleConfig('UgcExpandedPage')`.
 *
 * The `seeAll*` parts intentionally mirror ProductGrid's `showMore*` parts so
 * the "SEE ALL" CTA is pixel-identical to the PLP "Show More" button. That
 * button uses `variant="webPrimary"`, which takes its casing from
 * `textStyles.webButtonTypography` — uppercase in the base (Garage) theme,
 * `textTransform: 'none'` for Dynamite — so "SEE ALL" only uppercases on Garage.
 */
export default {
    parts: [
        'container',
        'breadcrumb',
        'styledByYouLink',
        'layout',
        'mediaColumn',
        'mediaSticky',
        'mediaFrame',
        'mediaImage',
        'mediaCreatorBadge',
        'productColumn',
        'shopTheLookTitle',
        'moreLooksTitle',
        'moreLooksGrid',
        'relatedTile',
        'relatedTileLink',
        'relatedTileFrame',
        'relatedTileImage',
        'seeAllContainer',
        'seeAllButton',
        'mobileContainer',
        'mobileMedia',
        'mobileTrack',
        'mobileSlide',
        'mobileMediaImage',
        'mobileCreatorBadge',
        'mediaGradient',
        'swipeHint',
        'swipeHintChevron',
        'swipeHintText',
        'drawer',
        'drawerToggle',
        'drawerToggleLabel',
        'drawerBody'
    ],
    baseStyle: {
        container: {
            paddingX: {base: 4, lg: 6},
            paddingTop: {base: 4, lg: 6},
            paddingBottom: '64px'
        },
        breadcrumb: {marginBottom: 4},
        styledByYouLink: {
            display: 'inline-block',
            fontSize: '12px',
            marginBottom: 4,
            textDecoration: 'none',
            _hover: {textDecoration: 'none'},
            _focusVisible: {
                outline: '2px solid',
                outlineColor: 'black',
                outlineOffset: '2px'
            }
        },
        layout: {
            display: 'grid',
            gridTemplateColumns: {base: '1fr', lg: '6fr 5fr', xl: '5fr 5fr'},
            gap: {base: 4, lg: 3, xl: 'min(180px, 9%)'},
            maxWidth: {xl: '1980px'},
            marginX: {xl: 'auto'}
        },
        mediaColumn: {position: 'relative', maxWidth: {lg: '560px', xl: '780px'}},
        mediaSticky: {position: 'sticky', top: 0},
        mediaFrame: {aspectRatio: '2 / 3', position: 'relative', overflow: 'hidden', bg: 'gray.50'},
        mediaImage: {width: '100%', height: '100%', objectFit: 'cover'},
        // Creator credit overlaid on the influencer image/video, bottom-left of
        // the media frame — not part of the page flow. `pointerEvents: none`
        // keeps the video's own controls clickable underneath.
        mediaCreatorBadge: {
            position: 'absolute',
            left: '16px',
            bottom: '22px',
            zIndex: 2,
            color: 'white',
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            pointerEvents: 'none'
        },
        productColumn: {maxWidth: {lg: '450px', xl: '780px'}},
        // Realm 1 (SFRA) expanded-view typography, Garage values. The family is
        // the brand-aware `fonts.heading` token — GarageNeue on Garage, AzoSans
        // on Dynamite — so only the deltas (regular weight, `text-transform:
        // none`) live in the Dynamite brand override:
        // theme/brands/dynamite/components/project/ugc-expanded-page.js
        shopTheLookTitle: {
            color: 'black',
            fontFamily: 'heading',
            fontSize: '16px',
            fontWeight: 'bold',
            lineHeight: 1,
            letterSpacing: 'normal',
            margin: '0 0 16px',
            padding: 0,
            textTransform: 'uppercase'
        },
        moreLooksTitle: {
            color: 'black',
            fontFamily: 'heading',
            fontSize: '16px',
            fontWeight: 'normal',
            lineHeight: 1,
            letterSpacing: 'normal',
            margin: '32px 0 16px',
            padding: 0,
            textTransform: 'uppercase'
        },
        moreLooksGrid: {
            display: 'grid',
            gridTemplateColumns: {
                base: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
            },
            gap: {base: 2, md: 3, lg: 4}
        },
        relatedTile: {position: 'relative'},
        relatedTileLink: {
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            textDecoration: 'none',
            _hover: {textDecoration: 'none'},
            _focusVisible: {
                outline: '2px solid',
                outlineColor: 'black',
                outlineOffset: '2px',
                zIndex: 2
            }
        },
        relatedTileFrame: {aspectRatio: '2 / 3', overflow: 'hidden', bg: 'gray.50'},
        relatedTileImage: {width: '100%', height: '100%', objectFit: 'cover'},
        // Same values as ProductGrid.showMoreContainer / UgcLandingPage.showMoreContainer.
        seeAllContainer: {marginTop: {base: 12, lg: 16}},
        seeAllButton: {width: {base: '100%'}, maxWidth: {base: '100%', lg: '342px'}},

        // Mobile
        mobileContainer: {
            position: 'relative',
            height: 'calc(100dvh - 85px)',
            bg: 'black',
            overflow: 'hidden',
            overscrollBehavior: 'none'
        },
        mobileMedia: {
            position: 'relative',
            width: '100%',
            height: '100%',
            touchAction: 'none',
            _focusVisible: {
                outline: '2px solid',
                outlineColor: 'white',
                outlineOffset: '-4px'
            }
        },
        mobileTrack: {
            position: 'absolute',
            inset: 0,
            willChange: 'transform'
        },
        mobileSlide: {
            position: 'absolute',
            inset: 0,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        },
        mobileMediaImage: {width: '100%', height: '100%', objectFit: 'cover'},
        mediaGradient: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            transition: 'background 0.6s ease'
        },
        mobileCreatorBadge: {
            position: 'absolute',
            left: '16px',
            // Clears the collapsed Shop-The-Look sheet handle (56px) plus the
            // 22px gap the design calls for.
            bottom: '78px',
            zIndex: 2,
            color: 'white',
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            pointerEvents: 'none'
        },
        swipeHint: {
            position: 'absolute',
            left: '50%',
            bottom: '126px',
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            color: 'white',
            fontSize: '12px',
            textAlign: 'center',
            pointerEvents: 'none'
        },
        swipeHintText: {whiteSpace: 'pre-line'},
        swipeHintChevron: {
            boxSize: 5,
            css: {
                '@keyframes ugcSwipeHintBounce': {
                    '0%, 100%': {transform: 'translateY(0)'},
                    '50%': {transform: 'translateY(-8px)'}
                },
                animation: 'ugcSwipeHintBounce 1.2s ease-in-out infinite',
                '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
            }
        },
        drawer: {
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            height: '85dvh',
            bg: 'white',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
        },
        drawerToggle: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minHeight: '56px',
            flexShrink: 0,
            padding: 4,
            gap: 4,
            bg: 'white',
            borderBottom: '1px solid',
            borderColor: 'gray.100',
            _focusVisible: {
                outline: '2px solid',
                outlineColor: 'black',
                outlineOffset: '-2px'
            }
        },
        drawerToggleLabel: {fontWeight: 'bold', fontSize: '14px', textTransform: 'uppercase'},
        drawerBody: {overflowY: 'auto', paddingX: 4, paddingBottom: 6, flex: '1'}
    }
}
